import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { modelClass, Project } from '/imports/api/projects';
import { FormField } from '/imports/ui/FormField';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { Input } from '/imports/ui/Input';
import { Form } from '/imports/ui/Form';
import { FileInput } from '/imports/ui/FileInput';
import { copyUtilityCredentialsLink, toastOnError } from '/imports/ui/utils';
import { Button } from '/imports/ui/Button';
import { css } from '/imports/ui/css';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { randomString } from '/imports/utils';
import { Select } from '/imports/ui/Select';
import { Icon } from '/imports/ui/Icon';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { useMethod, useIsLuminia } from '/imports/hooks';
import { Table } from '/imports/ui/Table';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import {
  approveProjectDocument, DocumentTypeOptions, listRequiredProjectDocuments, ProjectDocument,
  modelClass as projectDocumentClass, projectDocumentType
} from '/imports/api/projectDocuments';

const DataRoomSectionStyled = styled.div`
  margin-top: 1rem;
  .instructions-and-upload {
    display: flex;
    gap: 2rem;
    .instructions {
      flex: 2;
      table {
        text-align: left;
        position: relative;
        margin-top: -2rem;
        td {
          padding-right: 2rem;
        }
      }
    }
    .upload {
      flex: 1;
      .field-group {
        margin-bottom: 0.5rem;
      }
      .submit {
        margin-top: auto;
        flex: 0;
      }
    }
  }
  .save {
    margin-top: auto;
  }
  .helioscope-field {
    max-width: 32rem;
  }
  .helioscope-input {
    &.disabled {
      input {
        background: ${css.grey7};
        cursor: not-allowed;
      }
    }
  }
  .new-document-name {
    max-width: 32rem;
  }
  .new-document-type {
    max-width: 32rem;
  }
  .list-wrapper {
    margin-top: 2rem;
    width: 100%;
    max-height: 25rem;
    display: flex;
    .row-select, .row-actions {
      display: none;
    }
    .actions {
      display: flex;
      gap: 0.5rem;
    }
  }
  .catalog {
    width: 100%;
    height: 100%;
    max-height: 25rem;
  }
  .approved-icon {
    svg {
      fill: ${css.green} !important;
      stroke: ${css.green} !important;
    }
  }
  .required-documents {
    .icon {
      cursor: pointer;
    }
  }
  .hidden {
    display: none;
  }
  .loading-fullscreen {
    opacity: 0.4;
  }
`;

/** Project data-room section: helioscope link, required documents, and other document uploads. */
export const DataRoomSection = (props: { project: Project }) => {
  const isLuminia = useIsLuminia();
  const { project } = props;
  const projectId = project._id;
  const [update, setUpdate] = useState<Partial<Project>>({ _id: project._id });
  const [editing, setEditing] = useState(false);
  const [projectDocument, setProjectDocument] = useState<Partial<ProjectDocument>>();
  const [updateDocumentsList, setUpdateDocumentsList] = useState(randomString());
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (editing) document.querySelector<HTMLInputElement>('#helioscope-input')?.select();
  }, [editing]);
  /** Saves the project update (helioscope link) and exits edit mode. */
  const submitUpdate = toastOnError(async () => {
    // TODO: validate that the helioscope link is valid.
    await modelClass.updateMethod.call(update);
    toast.success(strings.Saved());
    setEditing(false);
  });
  /**
   * Creates a project document and refreshes the documents list.
   * @param _projectDocument - document to create; falls back to the staged projectDocument state
   */
  const submitDocument = toastOnError(async (_projectDocument?: Partial<ProjectDocument>) => {
    const __projectDocument = _projectDocument || projectDocument;
    if (!__projectDocument) return;
    await projectDocumentClass.createMethod.call(__projectDocument);
    toast.success(strings.Saved());
    setEditing(false);
    setUploading(false);
    setUpdateDocumentsList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setProjectDocument(undefined));
  const [deletingDocument, setDeletingDocument] = useState<ProjectDocument>();
  /**
   * Deletes a project document and refreshes the list.
   * @param projectDocument - document to delete
   */
  const deleteDocument = toastOnError(async (projectDocument: ProjectDocument) => {
    await projectDocumentClass.deleteMethod.call(projectDocument._id);
    toast.success(strings.Deleted());
    setUpdateDocumentsList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setDeletingDocument(undefined));
  /**
   * Approves a project document and refreshes the list.
   * @param documentId - id of the document to approve
   */
  const approveDocument = toastOnError(async (documentId: string) => {
    await approveProjectDocument.call({ documentId });
    toast.success(strings.Approved());
    setUpdateDocumentsList(randomString());
  });
  const [requiredDocuments, loadingRequiredDocuments] = useMethod(listRequiredProjectDocuments, { projectId }, [updateDocumentsList]);
  const {
    intervalDataDocument, helioscopeDocument, avoidedCostDocument, siteSurveyDocument,
    epcQuoteDocument
  } = requiredDocuments || {};
  const requiredDocumentIds = loadingRequiredDocuments ? [''] : [
    intervalDataDocument?._id || '',
    helioscopeDocument?._id || '',
    avoidedCostDocument?._id || '',
    siteSurveyDocument?._id || '',
    epcQuoteDocument?._id || '',
  ];
  const Catalog = useMemo(() => (
    <ListMethodCatalog
      key={updateDocumentsList}
      data={[]}
      columns={[
        { field: 'name' }, { field: 'type', render: (d) => projectDocumentType(d) }, { field: 'createdAt' },
        {
          field: 'actions', label: ' ', render: (d) => {
            return (
              <span className='actions'>
                <a href={fileHref(d.file)} target="_blank" rel="noreferrer"><Icon icon="documentSearch" tip={strings.OpenFile()} /></a>
                <a href="#"><Icon icon="cancel" onClick={() => setDeletingDocument(d)} tip={strings.DeleteDocument()} /></a>
              </span>
            );
          }
        },
      ]}
      query={{ projectId, _id: { $nin: requiredDocumentIds } }}
      defaultSort={{ createdAt: -1 }}
      listMethod={projectDocumentClass.listMethod}
    />
  ), [updateDocumentsList, JSON.stringify(requiredDocumentIds)]);
  /** Action cell for a required document: open, upload, approve, delete. */
  const RequiredDocumentActions = (props: { doc?: ProjectDocument, type?: ProjectDocument['type'], extra?: React.ReactNode }) => {
    const { doc, type, extra } = props;
    const fileInputId = `id${randomString()}`;
    return (
      <td>
        {
          doc ?
            <a href={fileHref(doc.file)} target="_blank" rel="noreferrer"><Icon icon="documentSearch" tip={strings.OpenFile()} /></a>
            :
            <Icon icon="upload" tip={strings.Upload()} onClick={() => window.document.getElementById(fileInputId)?.click()} />
        }
        <FileInput
          id={fileInputId} className="hidden"
          onChange={(file) => submitDocument({ projectId, name: file.name, file, type })}
          onUploadStart={() => setUploading(true)} onUploadDone={() => setUploading(false)}
        />
        {
          isLuminia && doc ?
            <a href="#">
              {
                doc.approvedAt ?
                  <Icon icon="circleCheck" className="approved-icon" tip={strings.Approved()} />
                  :
                  <Icon icon="check" onClick={() => approveDocument(doc._id)} tip={strings.Approve()} />
              }
            </a>
            : null
        }
        {
          doc ?
            <a href="#"><Icon icon="cancel" onClick={() => setDeletingDocument(doc)} tip={strings.DeleteDocument()} /></a>
            :
            null
        }
        {extra}
      </td>
    );
  };
  /** Status cell for a required document (approved / uploaded / none). */
  const RequiredDocumentStatus = (props: { doc?: ProjectDocument }) => (
    <td>{props.doc?.status === 'approved' ? strings.Approved() : (props.doc ? strings.Uploaded() : '-')}</td>
  );
  return (
    <DataRoomSectionStyled>
      <div className="helioscope">
        <p dangerouslySetInnerHTML={{ __html: strings.HelioscopeInstructions() }} />
        <Form onSubmit={() => submitUpdate()}>
          <div className="field-group">
            <FormField field="helioscopeLink" className="helioscope-field">
              <label>{strings.HelioscopeLink()}</label>
              <Input className={`helioscope-input${editing ? ' editing' : ' disabled'}`} inputId="helioscope-input"
                value={update.helioscopeLink === undefined ? project.helioscopeLink : update.helioscopeLink}
                onChange={(helioscopeLink) => setUpdate({ ...update, helioscopeLink })}
                onClick={() => !editing && setEditing(true)} tip={strings.ClickToEdit()} />
            </FormField>
            {
              editing ?
                <Button className="btn primary save" type="submit">{strings.Save()}</Button>
                :
                <Button className="btn primary save" onClick={(e) => { e.preventDefault(); setEditing(true); }}>{strings.Edit()}</Button>
            }
          </div>
        </Form>
      </div>
      <div className="required-documents" key={updateDocumentsList}>
        <h3>{strings.RequiredDocuments()}</h3>
        <p>{strings.RequiredDocumentsInstructions()}</p>
        <Table>
          <table>
            <thead>
              <tr>
                <th>{strings.Document()}</th><th>{strings.Actions()}</th><th>{strings.Status()}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{strings.OneYearFifteenMinuteUsageData()}</td>
                <RequiredDocumentActions doc={intervalDataDocument} type="intervalData" extra={
                  intervalDataDocument ?
                    null
                    :
                    <Icon icon="link2" tip={strings.CopyLinkToConnectUtilityAccount()} onClick={() => copyUtilityCredentialsLink(project._id)} />
                } />
                <RequiredDocumentStatus doc={intervalDataDocument} />
              </tr>
              <tr>
                <td>{strings.HelioscopeReport()}</td>
                <RequiredDocumentActions doc={helioscopeDocument} type="helioscope" />
                <RequiredDocumentStatus doc={helioscopeDocument} />
              </tr>
              <tr>
                <td>{strings.AvoidedCostAnalysis()}</td>
                <RequiredDocumentActions doc={avoidedCostDocument} type="avoidedCost" />
                <RequiredDocumentStatus doc={avoidedCostDocument} />
              </tr>
              <tr>
                <td>{strings.SiteSurvey()}</td>
                <RequiredDocumentActions doc={siteSurveyDocument} type="siteSurvey" />
                <RequiredDocumentStatus doc={siteSurveyDocument} />
              </tr>
              <tr>
                <td>{strings.EpcQuote()}</td>
                <RequiredDocumentActions doc={epcQuoteDocument} type="epcQuote" />
                <RequiredDocumentStatus doc={epcQuoteDocument} />
              </tr>
            </tbody>
          </table>
        </Table>
      </div>
      <div className="documents">
        <h3>{strings.OtherDocuments()}</h3>
        <div className="instructions-and-upload">
          <div className="upload">
            {
              projectDocument ?
                <Form onSubmit={() => submitDocument()}>
                  <div className="field-group">
                    <FormField field="name" className="new-document-name">
                      <label>{strings.Name()}</label>
                      <Input value={projectDocument?.name} onChange={(name) => setProjectDocument({ ...projectDocument, name })} />
                    </FormField>
                    <FormField field="type" className="new-document-type">
                      <label>{strings.Type()}</label>
                      <Select
                        value={projectDocument?.type}
                        options={[{ value: '', label: '' }, ...DocumentTypeOptions]}
                        onChange={(type: ProjectDocument['type']) => setProjectDocument({ ...projectDocument, type })}
                      />
                    </FormField>
                    <FormField field="submit" className="submit">
                      <Button type="submit">{strings.Submit()}</Button>
                    </FormField>
                  </div>
                </Form>
                :
                <FileInput className="file-input" onChange={(file) => setProjectDocument({ projectId, name: file.name, file })} />
            }
          </div>

        </div>
        <div className="list-wrapper">
          {Catalog}
        </div>
      </div>
      {
        deletingDocument ?
          <ConfirmModal
            title={strings.DeleteDocument()}
            body={
              <div>{strings.AreYouSureYouWantToDeleteTheDocument()}<br /><b>{deletingDocument.name}</b></div>
            }
            onClose={() => setDeletingDocument(undefined)}
            onConfirm={() => deleteDocument(deletingDocument)}
          />
          :
          null
      }
      {
        uploading ?
          <div className="uploading-overlay"><LoadingFullScreen caption={strings.Uploading()} /></div>
          :
          null
      }
    </DataRoomSectionStyled >
  );
};